import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArticleIcon from '@mui/icons-material/Article';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import WebhookIcon from '@mui/icons-material/Webhook';
import { alpha, Theme } from '@mui/material/styles';
import { createAppScope, AppEntity, permission } from '@nohara-dxdev/oidc-core';
import {
  BreadcrumbItem,
  DashboardLayout,
  DashboardNavMenuItem,
  useIsAppAdmin,
  useIsSystemAdmin,
  useScopeIds,
  useScope,
} from '@nohara-dxdev/react-oidc';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import { paths } from '@/config/paths';

const permissions = {
  readRole: [permission.role.read],
  readUser: [permission.user.read],
  readApp: [permission.app.read],
  readOrg: [permission.org.read],
  readWebhook: [permission.webhook.read],
  readEmailTemplate: [permission.emailTemplate.read],
  readTerms: [permission.terms.read],
};

export const AppLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { entities } = useScope();
  const context = useScopeIds();
  const appId = context.appId;
  const app = appId ? (entities.get('app') as AppEntity) : undefined;

  const isSystemAdmin = useIsSystemAdmin();
  const isAppAdmin = useIsAppAdmin(appId);

  const appScope = useMemo(() => (appId ? createAppScope(appId) : undefined), [appId]);

  const sections = useMemo((): DashboardNavMenuItem[][] => {
    const mainSection = appId
      ? ([
          {
            path: paths.app.dashboard.getHref(appId),
            icon: <DashboardIcon />,
            label: t('navigation.menu.dashboard'),
            context,
          },
          {
            path: paths.app.organizations.getHref(appId),
            icon: <BusinessIcon />,
            label: t('navigation.menu.organizations'),
            requireAll: permissions.readOrg,
            scope: appScope,
            context,
          },
          {
            path: paths.app.roles.getHref(appId),
            icon: <BadgeIcon />,
            label: t('navigation.menu.roles'),
            requireAll: permissions.readRole,
            scope: appScope,
            context,
          },
          {
            path: paths.app.users.getHref(appId),
            icon: <PeopleIcon />,
            label: t('navigation.menu.users'),
            requireAll: permissions.readUser,
            scope: appScope,
            context,
          },
          ...(isAppAdmin
            ? [
                {
                  path: paths.app.webhooks.getHref(appId),
                  icon: <WebhookIcon />,
                  label: t('navigation.menu.webhooks'),
                  requireAll: permissions.readWebhook,
                  scope: appScope,
                  context,
                },
              ]
            : []),
          {
            path: paths.app.emailTemplate.getHref(appId),
            icon: <EmailIcon />,
            label: t('navigation.menu.emailTemplate'),
            requireAll: permissions.readEmailTemplate,
            scope: appScope,
            context,
          },
          {
            path: paths.app.terms.getHref(appId),
            icon: <ArticleIcon />,
            label: t('navigation.menu.terms'),
            requireAll: permissions.readTerms,
            scope: appScope,
            context,
          },
          {
            path: paths.app.appProfile.getHref(appId),
            icon: <SettingsIcon />,
            label: t('navigation.menu.appProfile'),
            requireAll: permissions.readApp,
            scope: appScope,
            context,
          },
        ] satisfies DashboardNavMenuItem[])
      : [];

    return mainSection.length > 0 ? [mainSection] : [];
  }, [isAppAdmin, appId, appScope, t, context]);

  const bottomSections = useMemo((): DashboardNavMenuItem[][] => {
    const items: DashboardNavMenuItem[] = [];

    if (isSystemAdmin) {
      items.push({
        path: paths.admin.root.path,
        icon: <AdminPanelSettingsIcon />,
        label: t('navigation.menu.adminPanel'),
      });
    }

    return items.length > 0 ? [items] : [];
  }, [isSystemAdmin, t]);

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    if (appId) {
      crumbs.push({
        label: app?.name ?? t('navigation.appTitle'),
        href: paths.app.dashboard.getHref(appId),
      });
    }
    return crumbs;
  }, [appId, app?.name, t]);

  return (
    <DashboardLayout
      sections={sections}
      bottomSections={bottomSections}
      navTitle={app?.name ?? t('navigation.appTitle')}
      pageTitle={app?.name ?? t('navigation.appAppTitle')}
      breadcrumbs={breadcrumbs}
      paths={{
        logout: paths.auth.login.path,
        userProfile: paths.org.userProfile.getHref(),
      }}
      appBarSx={{
        bgcolor: 'success.main',
      }}
      drawerSx={{
        '& .MuiDrawer-paper': {
          bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.05),
        },
      }}
      appVersion={`${t('common.appVersion')}: ${env.APP_VERSION}`}
      backendVersion={`${t('common.backendVersion')}: ${env.BACKEND_VERSION}`}
    >
      {children}
    </DashboardLayout>
  );
};
